import { createSelector } from 'redux-orm';

import orm from '../orm';
import { BoardMembershipRoles, ResourceTypes } from '../constants/Enums';
import { isLocalId } from '../utils/local-id';

export const selectCurrentUserId = ({ auth: { userId } }) => userId;

export const selectUsers = createSelector(orm, ({ User }) =>
  User.getOrderedUndeletedQuerySet().toRefArray(),
);

export const selectUsersExceptCurrent = createSelector(
  orm,
  (state) => selectCurrentUserId(state),
  ({ User }, id) =>
    User.getOrderedUndeletedQuerySet()
      .exclude({
        id,
      })
      .toRefArray(),
);

export const selectCurrentUser = createSelector(
  orm,
  (state) => selectCurrentUserId(state),
  ({ User }, id) => {
    if (!id) {
      return id;
    }

    const userModel = User.withId(id);

    if (!userModel) {
      return userModel;
    }

    return userModel.ref;
  },
);

// Selecteur pour savoir si la ressource est dispo pour l'utilisateur courant
export const selectAvailableResourcesForCurrentUser = createSelector(
  orm,
  (state) => selectCurrentUserId(state),
  ({ User, Project, Board, Card }, userId) => {
    if (!userId) {
      return {
        projectIds: new Set(),
        boardIds: new Set(),
        cardIds: new Set(),
      };
    }

    const userModel = User.withId(userId);

    if (!userModel) {
      return {
        projectIds: new Set(),
        boardIds: new Set(),
        cardIds: new Set(),
      };
    }

    const projectIds = new Set();
    const boardIds = new Set();
    const cardIds = new Set();

    userModel.roleAssignments.toModelArray().forEach((assignment) => {
      switch (assignment.resourceType) {
        case ResourceTypes.PROJECT: {
          const project = Project.withId(assignment.resourceId);

          if (project) {
            projectIds.add(project.id);

            project.boards.toModelArray().forEach((board) => {
              boardIds.add(board.id);

              board.cards.toModelArray().forEach((card) => {
                cardIds.add(card.id);
              });
            });
          }

          break;
        }

        case ResourceTypes.BOARD: {
          const board = Board.withId(assignment.resourceId);

          if (board) {
            boardIds.add(board.id);

            if (board.projectId) {
              projectIds.add(board.projectId);
            }

            board.cards.toModelArray().forEach((card) => {
              cardIds.add(card.id);
            });
          }

          break;
        }

        case ResourceTypes.CARD: {
          const card = Card.withId(assignment.resourceId);

          if (card) {
            cardIds.add(card.id);

            if (card.boardId) {
              boardIds.add(card.boardId);

              const board = Board.withId(card.boardId);

              if (board?.projectId) {
                projectIds.add(board.projectId);
              }
            }
          }

          break;
        }

        default:
      }
    });

    return {
      projectIds,
      boardIds,
      cardIds,
    };
  },
);

export const selectProjectsForCurrentUser = createSelector(
  orm,
  (state) => selectCurrentUserId(state),
  selectAvailableResourcesForCurrentUser,
  ({ User, Project }, id, { projectIds, boardIds }) => {
    if (!id) {
      return id;
    }

    const userModel = User.withId(id);

    if (!userModel) {
      return userModel;
    }

    const projectModels = [...projectIds]
      .map((projectId) => Project.withId(projectId))
      .filter(Boolean);

    if (userModel.organizationId) {
      const orgProject = Project.all()
        .toModelArray()
        .find((p) => p.organizationId === userModel.organizationId);

      if (orgProject && !projectModels.some((p) => p.id === orgProject.id)) {
        projectModels.push(orgProject);
      }
    }

    return projectModels.map((projectModel) => {
      const boardsModels = projectModel
        .getOrderedBoardsQuerySet()
        .toModelArray()
        .filter((boardModel) => boardIds.has(boardModel.id));

      let notificationsTotal = 0;

      boardsModels.forEach((boardModel) => {
        boardModel.cards.toModelArray().forEach((cardModel) => {
          notificationsTotal += cardModel.getUnreadNotificationsQuerySet().count();
        });
      });

      return {
        ...projectModel.ref,
        notificationsTotal,
        firstBoardId: boardsModels[0]?.id,
      };
    });
  },
);

export const selectProjectsToListsForCurrentUser = createSelector(
  orm,
  (state) => selectCurrentUserId(state),
  selectAvailableResourcesForCurrentUser,
  ({ User, Project }, id, { projectIds }) => {
    if (!id) {
      return id;
    }

    const userModel = User.withId(id);

    if (!userModel) {
      return userModel;
    }

    const projectModels = [...projectIds]
      .map((projectId) => Project.withId(projectId))
      .filter(Boolean);

    return projectModels.map((projectModel) => ({
      ...projectModel.ref,
      boards: projectModel
        .getOrderedBoardsQuerySet()
        .toModelArray()
        // .filter((boardModel) => (selectAvailableResourcesForCurrentUser.resultFunc ? true : true))
        .map((boardModel) => ({
          ...boardModel.ref,
          lists: boardModel.getOrderedListsQuerySet().toRefArray(),
        })),
    }));
  },
);

export const selectNotificationsForCurrentUser = createSelector(
  orm,
  (state) => selectCurrentUserId(state),
  ({ User }, id) => {
    if (!id) {
      return id;
    }

    const userModel = User.withId(id);

    if (!userModel) {
      return userModel;
    }

    return userModel
      .getOrderedUnreadNotificationsQuerySet()
      .toModelArray()
      .map((notificationModel) => ({
        ...notificationModel.ref,
        activity: notificationModel.activity && {
          ...notificationModel.activity.ref,
          user: notificationModel.activity.user.ref,
        },
        card: notificationModel.card && notificationModel.card.ref,
      }));
  },
);

export const selectUserDefaultProject = createSelector(
  orm,
  (state) => selectCurrentUserId(state),
  ({ Project, User }, id) => {
    const userModel = User.withId(id);

    if (!userModel) {
      return userModel;
    }

    const allProjects = Project.all().toRefArray();
    return allProjects.find((project) => project.organizationId === userModel.organizationId);
  },
);

export const selectSearchedUsers = (state) => state.core.searchedUsers;
export const selectIsSearchingUsers = (state) => state.core.isSearchingUsers;

export const selectCurrentUserRoleAssignments = createSelector(
  orm,
  (state) => selectCurrentUserId(state),
  ({ UserRoleAssignment }, currentUserId) => {
    const assignments = UserRoleAssignment.all()
      .toRefArray()
      .filter((assignment) => assignment.userId === currentUserId);

    console.log('[RBAC] Current user assignments', assignments);

    return assignments;
  },
);

export const selectCurrentUserRoles = createSelector(
  orm,
  (state) => selectCurrentUserId(state),
  ({ UserRoleAssignment, Role }, currentUserId) => {
    const assignments = UserRoleAssignment.all()
      .toRefArray()
      .filter((assignment) => assignment.userId === currentUserId);

    const roleIds = assignments.map((assignment) => assignment.roleId);

    const roles = Role.all()
      .toRefArray()
      .filter((role) => roleIds.includes(role.id));

    console.log('[RBAC] Current user roles', roles);

    return roles;
  },
);

const resolvePermissionsForResource = (
  UserRoleAssignment,
  RolePermission,
  Permission,
  currentUserId,
  resourceType,
  resourceId,
) => {
  const assignments = UserRoleAssignment.all()
    .toRefArray()
    .filter(
      (assignment) =>
        assignment.userId === currentUserId &&
        assignment.resourceType === resourceType &&
        assignment.resourceId === resourceId,
    );

  const roleIds = assignments.map((assignment) => assignment.roleId);

  const permissionIds = RolePermission.all()
    .toRefArray()
    .filter((rp) => roleIds.includes(rp.roleId))
    .map((rp) => rp.permissionId);

  return Permission.all()
    .toRefArray()
    .filter((permission) => permissionIds.includes(permission.id))
    .map((permission) => permission.code);
};

export const selectHasPermission = createSelector(
  orm,
  (state) => selectCurrentUserId(state),
  (_, resourceType) => resourceType,
  (_, __, resourceId) => resourceId,
  (_, __, ___, permissionCode) => permissionCode,

  (
    { UserRoleAssignment, RolePermission, Permission },
    currentUserId,
    resourceType,
    resourceId,
    permissionCode,
  ) => {
    const assignments = UserRoleAssignment.all()
      .toRefArray()
      .filter(
        (assignment) =>
          assignment.userId === currentUserId &&
          assignment.resourceType === resourceType &&
          assignment.resourceId === resourceId,
      );

    const roleIds = assignments.map((assignment) => assignment.roleId);

    const rolePermissions = RolePermission.all()
      .toRefArray()
      .filter((rp) => roleIds.includes(rp.roleId));

    const permissionIds = rolePermissions.map((rp) => rp.permissionId);

    const hasPermission = Permission.all()
      .toRefArray()
      .some(
        (permission) => permissionIds.includes(permission.id) && permission.code === permissionCode,
      );

    console.log('[RBAC] Permission check', {
      currentUserId,
      resourceType,
      resourceId,
      permissionCode,
      hasPermission,
    });

    return hasPermission;
  },
);

export const selectEffectivePermissionsForCard = createSelector(
  orm,
  (state) => selectCurrentUserId(state),
  (_, cardId) => cardId,

  ({ Card, UserRoleAssignment, RolePermission, Permission }, currentUserId, cardId) => {
    const cardModel = Card.withId(cardId);

    if (!cardModel) {
      return [];
    }

    const { boardId } = cardModel;
    const projectId = cardModel.board?.projectId;
    console.log(boardId);
    console.log(projectId);

    return [
      ...new Set([
        ...resolvePermissionsForResource(
          UserRoleAssignment,
          RolePermission,
          Permission,
          currentUserId,
          'Platform',
          0,
        ),

        ...resolvePermissionsForResource(
          UserRoleAssignment,
          RolePermission,
          Permission,
          currentUserId,
          'Project',
          projectId,
        ),

        ...resolvePermissionsForResource(
          UserRoleAssignment,
          RolePermission,
          Permission,
          currentUserId,
          'Board',
          boardId,
        ),

        ...resolvePermissionsForResource(
          UserRoleAssignment,
          RolePermission,
          Permission,
          currentUserId,
          'Card',
          cardId,
        ),
      ]),
    ];
  },
);

export default {
  selectCurrentUserId,
  selectUsers,
  selectUsersExceptCurrent,
  selectCurrentUser,
  selectProjectsForCurrentUser,
  selectProjectsToListsForCurrentUser,
  selectNotificationsForCurrentUser,
  selectUserDefaultProject,
  // selectEditableBoardsForCurrentUser,
  selectSearchedUsers,
  selectIsSearchingUsers,
  selectCurrentUserRoleAssignments,
  selectCurrentUserRoles,
  selectHasPermission,
  selectEffectivePermissionsForCard,
};
